import { Pressable } from "@/components/ui/pressable";
import { ChevronRight, Circle, Clock } from "lucide-react";
import { useMemo } from "react";
import { useTicketStatusChoices } from "@/modules/settings/tickets/useTicketWorkspaceSettings";
import { toneClass } from "@/modules/shared/status";
import {
  ticketStatusFilterOptions,
  type TicketStatusFilterId,
} from "@/modules/tickets/ticketInboxConfig";
import { cn } from "@/lib/utils";

const filterActiveClass = (id: string): string => {
  switch (id) {
    case "all":
      return "bg-foreground/10 text-foreground ring-1 ring-border dark:text-foreground";
    case "needs_reply":
      return "bg-destructive text-destructive-text ring-1 ring-destructive/40";
    case "new":
      return cn(toneClass("success", "soft"), "ring-1 ring-success/25");
    case "open":
      return cn(toneClass("info", "soft"), "ring-1 ring-info/25");
    case "waiting":
      return cn(toneClass("warning", "soft"), "ring-1 ring-warning/25");
    case "resolved":
      return "bg-muted text-foreground ring-1 ring-border dark:text-foreground";
    default:
      return cn(toneClass("muted", "border"), "ring-1 ring-border");
  }
};

type ClientTicketStatusFilterProps = {
  active: TicketStatusFilterId;
  counts: Record<string, number>;
  onSelect: (status: TicketStatusFilterId) => void;
};

export const ClientTicketStatusFilter = ({
  active,
  counts,
  onSelect,
}: ClientTicketStatusFilterProps) => {
  const statusChoices = useTicketStatusChoices();
  const items = useMemo(
    () => ticketStatusFilterOptions(statusChoices),
    [statusChoices],
  );

  return (
    <div className="flex flex-nowrap items-center gap-0.5 overflow-x-auto text-sm [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {items.map((item, index) => {
        const isActive = active === item.id;
        const count = counts[item.id] ?? 0;

        return (
          <div key={item.id} className="flex shrink-0 items-center gap-0.5">
            {index > 0 ? (
              <ChevronRight className="size-3 shrink-0 text-muted-foreground/45" />
            ) : null}
            <Pressable
              onClick={() => onSelect(item.id)}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-sm transition-colors",
                isActive
                  ? cn(filterActiveClass(item.id), "font-semibold")
                  : "font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground",
              )}
            >
              {item.id === "waiting" ? (
                <Clock className="size-3 shrink-0" />
              ) : null}
              {item.id === "resolved" ? (
                <Circle className="size-3 shrink-0" />
              ) : null}
              <span>{item.label}</span>
              {count > 0 ? (
                <span className="tabular-nums opacity-90">{count}</span>
              ) : null}
            </Pressable>
          </div>
        );
      })}
    </div>
  );
};
